import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, Eye, Clock, X, RotateCcw, ChevronRight,
  Sparkles, Tag, DollarSign, Edit, Heart, MessageSquare
} from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { formatDistanceToNow } from 'date-fns';
import { formatLargeNumber } from '../lib/utils';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Normalize image URLs (handle both string and object formats)
const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (typeof url === 'object' && url.url) return url.url;
  if (typeof url !== 'string') return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_BASE_URL}${url}`;
  return url;
};

// Default category images
const CATEGORY_IMAGES = {
  electronics: 'https://images.unsplash.com/photo-1750797308931-b0d261abb3d5?w=400&h=400&fit=crop',
  furniture: 'https://images.unsplash.com/photo-1673695008224-fa5a9d195dfb?w=400&h=400&fit=crop',
  clothing: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&h=400&fit=crop',
  vehicles: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=400&fit=crop',
  other: 'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=400&h=400&fit=crop',
};

// Condition display mapping
const CONDITIONS = {
  new: { label: 'New', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  like_new: { label: 'Like New', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  excellent: { label: 'Excellent', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  good: { label: 'Good', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  fair: { label: 'Fair', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  poor: { label: 'Poor', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  any: { label: 'Any', color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
};

export function WantListItem({
  listing,
  onClose,
  onReopen,
  onClick,
  isSelected = false,
  onToggleSelect = null,
}) {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const [hoveredMatch, setHoveredMatch] = useState(null);
  
  const isClosed = listing.status === 'closed';
  const matchesCount = listing.matches_count || 0;
  const matchesPreviews = listing.matches_preview || [];
  
  // Get listing image
  const getListingImage = () => {
    if (imgError) return CATEGORY_IMAGES[listing.category] || CATEGORY_IMAGES.other;
    const imgUrl = listing.images?.[0];
    return normalizeImageUrl(imgUrl) || CATEGORY_IMAGES[listing.category] || CATEGORY_IMAGES.other;
  };

  // Format price range
  const formatPrice = () => {
    const min = listing.min_price || listing.price_min || 0;
    const max = listing.max_price || listing.price_max || 0;
    if (min && max && min !== max) {
      return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
    }
    if (max) return `Up to $${max.toLocaleString()}`;
    if (min) return `From $${min.toLocaleString()}`;
    return 'Price negotiable';
  };

  // Format time ago
  const getTimeAgo = () => {
    if (!listing.created_at) return '';
    try {
      return formatDistanceToNow(new Date(listing.created_at), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const handleViewAllMatches = (e) => {
    e.stopPropagation();
    navigate(`/wants/${listing.id}/matches`);
  };

  const handleMatchClick = (e, matchId) => {
    e.stopPropagation();
    navigate(`/matches/${matchId}`);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/wtb/${listing.id}`);
  };

  const condition = CONDITIONS[listing.condition] || CONDITIONS.any;

  return (
    <div
      className={`bg-[var(--theme-card)] rounded-xl border transition-all duration-200 overflow-hidden ${isClosed ? 'opacity-70' : ''} ${
        isSelected
          ? 'border-[#1E3A5F] dark:border-[#C9956C] ring-1 ring-[#1E3A5F]/30 dark:ring-[#C9956C]/30'
          : 'border-[var(--theme-border)] hover:border-[var(--theme-border-hover)]'
      }`}
      data-testid={`want-list-item-${listing.id}`}
    >
      <div className="flex flex-col lg:flex-row">
        {/* Checkbox Column */}
        {onToggleSelect && (
          <div
            className={`hidden lg:flex items-center justify-center px-3 border-r border-[var(--theme-border)] cursor-pointer hover:bg-[var(--theme-hover)] transition-colors ${
              isSelected ? 'bg-[#1E3A5F]/5 dark:bg-[#C9956C]/5' : ''
            }`}
            onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={onToggleSelect}
              className="h-5 w-5 rounded border-2 border-[var(--theme-border-hover)] data-[state=checked]:bg-[#1E3A5F] data-[state=checked]:border-[#1E3A5F] data-[state=checked]:text-white"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Left: Image Section */}
        <div
          className="relative w-full lg:w-48 h-48 lg:h-auto flex-shrink-0 cursor-pointer"
          onClick={onClick}
        >
          <img
            src={getListingImage()}
            alt={listing.title}
            onError={() => setImgError(true)}
            className={`w-full h-full object-cover ${isClosed ? 'grayscale-[30%]' : ''}`}
          />
          {/* Mobile checkbox overlay */}
          {onToggleSelect && (
            <div
              className="lg:hidden absolute top-2 right-2 z-10"
              onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={onToggleSelect}
                className="h-6 w-6 rounded border-2 bg-white/90 dark:bg-black/70 border-[var(--theme-border-hover)] data-[state=checked]:bg-[#1E3A5F] data-[state=checked]:border-[#1E3A5F] data-[state=checked]:text-white shadow-sm"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {/* Status badge */}
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
              isClosed ? 'bg-[#6B7280]/90 text-white' : 'bg-[#1E3A5F]/90 text-white'
            }`}>
              {isClosed ? 'CLOSED' : 'WANTED'}
            </span>
          </div>
          {/* Views overlay */}
          <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs">
            <Eye className="w-3 h-3" />
            <span>{formatLargeNumber(listing.views || 0)}</span>
          </div>
        </div>

        {/* Middle: Want Details */}
        <div className="flex-1 p-4 min-w-0">
          <div className="flex flex-col h-full">
            {/* Top section - Title & Price */}
            <div className="mb-3">
              <h3 
                className="font-semibold text-[var(--theme-text-primary)] text-lg mb-1 cursor-pointer hover:text-[#1E3A5F] dark:hover:text-[#C9956C] transition-colors line-clamp-1"
                onClick={onClick}
              >
                {listing.title}
              </h3>
              <div className="flex items-center gap-3">
                <p className="text-xl font-bold text-[#059669]">{formatPrice()}</p>
                {listing.is_flexible === false && (
                  <span className="text-xs font-medium text-[#6B7280] bg-[#6B7280]/10 px-2 py-0.5 rounded">
                    Firm
                  </span>
                )}
                {listing.is_flexible === true && (
                  <span className="text-xs font-medium text-[#059669] bg-[#059669]/10 px-2 py-0.5 rounded">
                    Flexible
                  </span>
                )}
              </div>
            </div>
            
            {/* Middle section - Details grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3">
              {/* Condition */}
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-[var(--theme-text-muted)]" />
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${condition.color}`}>
                  {condition.label}
                </span>
              </div>
              
              {/* Location */}
              <div className="flex items-center gap-2 text-sm text-[var(--theme-text-muted)]">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{listing.location_name || listing.location || 'Any location'}</span>
              </div>
              
              {/* Category */}
              <div className="flex items-center gap-2 text-sm text-[var(--theme-text-muted)]">
                <span className="capitalize">{listing.category || 'Other'}</span>
              </div>
              
              {/* Time */}
              <div className="flex items-center gap-2 text-sm text-[var(--theme-text-muted)]">
                <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{getTimeAgo()}</span>
              </div>
            </div>

            {/* Bottom: Action buttons */}
            <div className="mt-auto flex flex-wrap items-center gap-2">
              <button
                onClick={handleEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#1E3A5F] dark:text-[#C9956C] bg-[#1E3A5F]/10 dark:bg-[#C9956C]/10 hover:bg-[#1E3A5F]/20 dark:hover:bg-[#C9956C]/20 rounded-lg transition-colors"
                data-testid={`edit-want-${listing.id}`}
              >
                <Edit className="w-3.5 h-3.5" />
                Edit
              </button>
              
              {!isClosed && onClose && (
                <button
                  onClick={(e) => { e.stopPropagation(); onClose(); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6B7280] bg-[#6B7280]/10 hover:bg-[#6B7280]/20 rounded-lg transition-colors"
                  data-testid={`close-want-${listing.id}`}
                >
                  <X className="w-3.5 h-3.5" />
                  No longer wanted
                </button>
              )}
              
              {isClosed && onReopen && (
                <button
                  onClick={(e) => { e.stopPropagation(); onReopen(); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#C9956C] bg-[#C9956C]/10 hover:bg-[#C9956C]/20 rounded-lg transition-colors"
                  data-testid={`reopen-want-${listing.id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reopen
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right: Matches Preview - Sellers with matching items */}
        <div className="lg:w-72 xl:w-80 p-4 border-t lg:border-t-0 lg:border-l border-[var(--theme-border)] bg-[var(--theme-hover)]/30">
          {matchesCount > 0 ? (
            <div className="h-full flex flex-col">
              {/* Match count header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#C9956C]" />
                  <span className="text-base font-bold text-[#C9956C]">
                    {formatLargeNumber(matchesCount)} {matchesCount === 1 ? 'match' : 'matches'} found
                  </span>
                </div>
              </div>
              
              {/* Match previews */}
              <div className="space-y-2 mb-3 flex-1">
                {matchesPreviews.slice(0, 3).map((match) => (
                  <button
                    key={match.match_id}
                    onClick={(e) => handleMatchClick(e, match.match_id)}
                    onMouseEnter={() => setHoveredMatch(match.match_id)}
                    onMouseLeave={() => setHoveredMatch(null)}
                    className="w-full flex items-center gap-3 p-2 rounded-lg bg-[var(--theme-card)] border border-[var(--theme-border)] hover:border-[#C9956C] transition-all text-left group"
                    data-testid={`match-preview-${match.match_id}`}
                  >
                    {/* Item thumbnail */}
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-[var(--theme-hover)] flex-shrink-0">
                      {match.image ? (
                        <img 
                          src={normalizeImageUrl(match.image)} 
                          alt={match.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-[var(--theme-text-muted)]" />
                        </div>
                      )}
                    </div>
                    
                    {/* Match info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--theme-text-primary)] truncate group-hover:text-[#1E3A5F] dark:group-hover:text-[#C9956C]">
                        {match.title}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-[var(--theme-text-muted)]">
                        {match.price && (
                          <span className="text-[#059669] font-medium">
                            ${match.price.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/messages/${match.match_id}`); }}
                        className="p-1.5 rounded-lg hover:bg-[#1E3A5F]/10 text-[var(--theme-text-muted)] hover:text-[#1E3A5F] transition-colors opacity-0 group-hover:opacity-100"
                        title="Open chat"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* View all link */}
              <button
                onClick={handleViewAllMatches}
                className="flex items-center justify-center gap-1 w-full py-2 text-sm font-medium text-[#1E3A5F] dark:text-[#C9956C] hover:bg-[var(--theme-hover)] rounded-lg transition-colors"
                data-testid={`view-all-matches-${listing.id}`}
              >
                View all matches
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-4">
              <div className="w-12 h-12 rounded-full bg-[var(--theme-hover)] flex items-center justify-center mb-2">
                <Sparkles className="w-5 h-5 text-[var(--theme-text-muted)]" />
              </div>
              <p className="text-sm font-medium text-[var(--theme-text-secondary)]">No matches yet</p>
              <p className="text-xs text-[var(--theme-text-muted)] mt-1 max-w-[200px]">
                We're searching for items that match your criteria
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WantListItem;
